import { useCallback, useContext, useRef, useState } from 'react';
import SpeechRecognition from 'react-speech-recognition';
import { useMutation } from '@tanstack/react-query';
import type { ChatResponseType } from '@trinity/chat';
import { ActiveChatSessionContext } from '@trinity/chat-components/components/Chat/context';
import { useGroupFeatures } from '@trinity/chat-components/containers/Chat/hooks';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient';
import { AudioPlayerContainer, useAudioPlayerRef, useAudioPlayerState, useBootstrapAudioPlayer } from '../AudioPlayer';
import type {
  TextToSpeechState,
  TextToSpeechAudioMetadata,
  VoiceModeContextValue,
} from './Context';
import {
  TextToSpeechStateContext,
  InitiateQuerySpeechToText,
  MicrophoneStateContext,
  OnTextToSpeechAudioReadyContext,
  StopTextToSpeechContext,
  VoiceModeContext,
  LoadingVoiceClipContext,
} from './Context';
import { MicrophoneStateContainer } from './MicrophoneState.Container';
import { useTTSState } from './useTTSState';

type Props = {
  children: React.ReactNode;
};

const defaultAudioMetadata: TextToSpeechAudioMetadata = {
  kolSearchId: null,
  queryId: null,
  loadingState: null,
};
const defaultTTSState: TextToSpeechState = {
  kolSearchId: null,
  queryId: null,
  state: null,
};

export const TopLevelTTSContainer = (props: Props) => {
  const features = useGroupFeatures();
  const ttsEnabled = features.chatReadAloud;
  const chatVoiceSource = features.chatVoiceSource;

  if (!ttsEnabled) {
    return (
      <MicrophoneStateContainer>
        <VoiceModeContainer>
          {props.children}
        </VoiceModeContainer>
      </MicrophoneStateContainer>
    );
  }


  if (chatVoiceSource === 'browser') {
    return (
      <MicrophoneStateContainer>
        <VoiceModeContainer>
          <AudioPlayerContainer>
            <BrowserTTSContainer {...props} />
          </AudioPlayerContainer>
        </VoiceModeContainer>
      </MicrophoneStateContainer>
    );
  }

  return (
    <MicrophoneStateContainer>
      <VoiceModeContainer>
        <AudioPlayerContainer defaultMetadata={defaultAudioMetadata}>
          <TextToSpeechAudioPlayerContainer {...props} />
        </AudioPlayerContainer>
      </VoiceModeContainer>
    </MicrophoneStateContainer>
  );
};

const BrowserTTSContainer = ({ children }: Props) => {
  const [pendingAudio, setPendingAudio] = useState<TextToSpeechState>(defaultTTSState);
  const chat = useContext(ActiveChatSessionContext);

  const stopTextToSpeech = useCallback((isNavigationAction?: boolean) => {
    speechSynthesis.cancel();
    setPendingAudio(defaultTTSState);
  }, []);

  const apiClient = useChatApiClient();

  const handleTextToSpeechInitiated = useCallback(async (data: Omit<TextToSpeechState, 'state'>) => {
    function handleTextAvailable(value: string) {
      const utterance = new SpeechSynthesisUtterance(value);
      utterance.lang = 'en-US';
      utterance.rate = 1;
      utterance.pitch = 1;
      speechSynthesis.speak(utterance);
      setPendingAudio({
        ...data,
        state: 'playing',
      });
    }

    if (data.queryId) {
      return apiClient.queries.getQueryResponseText({
        chatInstanceId: chat.id,
        queryId: data.queryId,
        tts: true,
      }).then(handleTextAvailable);
    } else if (data.kolSearchId) {
      return apiClient.kolSearch.getKolSearchProfileText({
        kolSearchId: data.kolSearchId,
        tts: true,
      }).then(handleTextAvailable);
    }
  }, [chat.id]);

  return (
    <InitiateQuerySpeechToText.Provider value={handleTextToSpeechInitiated}>
      <StopTextToSpeechContext.Provider value={stopTextToSpeech}>
        <TextToSpeechStateContext.Provider value={pendingAudio}>
          <VoiceModeContainer>
            {children}
          </VoiceModeContainer>
        </TextToSpeechStateContext.Provider>
      </StopTextToSpeechContext.Provider>
    </InitiateQuerySpeechToText.Provider>
  );
};

const TextToSpeechAudioPlayerContainer = ({ children }: Props) => {
  const [playerRef, setPlayerRef] = useAudioPlayerRef();
  const bootstrapAudioPlayer = useBootstrapAudioPlayer();
  const [micState, setMicState] = useContext(MicrophoneStateContext);
  const voicemode = useContext(VoiceModeContext);
  const ttsState = useTTSState();
  const [playerState, dispatchAudioPlayer] = useAudioPlayerState();
  const loadingVoiceClipAudioPlayer = useContext(LoadingVoiceClipContext);

  const isTtsStoppingRef = useRef(false);

  const handleTextToSpeechAudioReady = useCallback((audioUrl: string) => {
    loadingVoiceClipAudioPlayer.onStop();
    if (playerRef) {
      playerRef.src = audioUrl;
      if (!isTtsStoppingRef.current) {
        const playPromise = playerRef.play();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise.catch(err => {
            console.error('Error playing audio:', err);
          });
        }
      }
      dispatchAudioPlayer({
        type: 'metadata-updated',
        metadata: {
          loadingState: 'loaded',
        },
      });

      const onEnded = () => {
        playerRef.removeEventListener('ended', onEnded);
      };

      playerRef.addEventListener('ended', () => {
        if (micState !== 'error') {
          if (voicemode.enabled && micState !== 'listening') {
            setMicState('waiting-for-permission');
            SpeechRecognition.startListening({ continuous: true });
          }
        }
        onEnded();
      });
    }
  }, [playerRef, dispatchAudioPlayer, micState, voicemode.enabled, setMicState]);

  const apiClient = useChatApiClient();

  const audioMutation = useMutation({
    mutationFn: (data: Omit<TextToSpeechState, 'state'>) => {
      return apiClient.audioStatus.startAsyncAudioSynthesis(data);
    },
    onMutate: async (data: Omit<TextToSpeechState, 'state'>) => {
      dispatchAudioPlayer({
        type: 'metadata-updated',
        metadata: {
          ...data,
          loadingState: 'loading',
        },
      });
    },
    onError: () => {
      dispatchAudioPlayer({
        type: 'metadata-updated',
        metadata: {
          loadingState: 'error',
        },
      });
    },
    onSuccess: (data) => {
      if (data.status === 'ready') {
        handleTextToSpeechAudioReady(data.audioUrl);
      }
    },
  });

  const handleTextToSpeechInitiated = useCallback(async (data: Omit<TextToSpeechState, 'state'>) => {
    const audioPlayerMetadata = (playerState?.metadata || null) as TextToSpeechAudioMetadata | null;

    if (!audioPlayerMetadata) {
      return audioMutation.mutateAsync(data);
    }

    if (audioPlayerMetadata.kolSearchId && data.kolSearchId && audioPlayerMetadata.kolSearchId === data.kolSearchId) {
      playerRef.play();
      return;
    }

    if (audioPlayerMetadata.queryId && data.queryId && audioPlayerMetadata.queryId === data.queryId) {
      playerRef.play();
      return;
    }

    return audioMutation.mutateAsync(data);
  }, [audioMutation, playerRef, playerState?.metadata]);

  const stopTextToSpeech = useCallback((isNavigationAction?: boolean) => {

    if (playerRef) {
      isTtsStoppingRef.current = true;
      try {
        if (!playerRef.paused) playerRef.pause();
        playerRef.currentTime = 0;
        playerRef.src = '';
        playerRef.load();
      } finally {
        setTimeout(() => { isTtsStoppingRef.current = false; }, 0);
      }
    }

    dispatchAudioPlayer({
      type: 'metadata-updated',
      metadata: defaultAudioMetadata,
    });

    if (voicemode?.enabled && !isNavigationAction && micState !== 'listening') {
      setMicState('waiting-for-permission');
      SpeechRecognition.startListening({ continuous: true });
    }
  }, [playerRef, voicemode?.enabled, micState, setMicState, dispatchAudioPlayer]);

  return (
    <TextToSpeechStateContext.Provider value={ttsState}>
      <OnTextToSpeechAudioReadyContext.Provider value={handleTextToSpeechAudioReady}>
        <InitiateQuerySpeechToText.Provider value={handleTextToSpeechInitiated}>
          <StopTextToSpeechContext.Provider value={stopTextToSpeech}>
            {children}
            <audio {...bootstrapAudioPlayer()} ref={setPlayerRef} />
          </StopTextToSpeechContext.Provider>
        </InitiateQuerySpeechToText.Provider>
      </OnTextToSpeechAudioReadyContext.Provider>
    </TextToSpeechStateContext.Provider>
  );
};

export { TextToSpeechAudioPlayerContainer };
export default TextToSpeechAudioPlayerContainer;

type VoiceModeContainerProps = {
  children: React.ReactNode;
};

const VoiceModeContainer = (props: VoiceModeContainerProps) => {
  const [voicemode, setVoiceMode] = useState(false);
  const [queryId, setQueryId] = useState<number>(null);
  const [type, setType] = useState<ChatResponseType>(null);
  const [_micState, _setMicState] = useContext(MicrophoneStateContext);

  const enableVoiceMode = useCallback(() => {
    setVoiceMode(true);
  }, []);

  const disableVoiceMode = useCallback(() => {
    setVoiceMode(false);
  }, []);

  const toggleVoiceMode = useCallback(() => {
    setVoiceMode(prev => {
      return !prev;
    });
  }, []);

  const enqueue = useCallback((data: { queryId: number; type: ChatResponseType }) => {
    setQueryId(data.queryId);
    setType(data.type);
  }, []);

  const dequeue = useCallback(() => {
    setQueryId(null);
  }, []);

  const vm: VoiceModeContextValue = {
    dequeue,
    enabled: voicemode,
    enable: enableVoiceMode,
    disable: disableVoiceMode,
    enqueue,
    queryId,
    toggle: toggleVoiceMode,
    type,
  };

  return (
    <VoiceModeContext.Provider value={vm}>
      {props.children}
    </VoiceModeContext.Provider>
  );
};
